/**
 * Phase 1 — anonymous listing teaser at /deal/:ulid.
 * Whitelisted payload only. Contextual gates: source → Free account,
 * full analysis/comparables → Pro (after registration).
 */
import React, { useEffect } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ExternalLink, Heart, LineChart, Lock, MapPin, Percent, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { getPublicListing } from "../api/publicApi";
import FinanceCalculator from "../components/FinanceCalculator";
import LeadModal from "../components/LeadModal";
import { MarketBadge } from "../components/public/PublicDealCard";
import { displayComparableCount, resolveMarketBadge } from "../utils/marketSignal";
import { trackFunnel } from "../lib/funnel";

function Fact({ label, value, testid }) {
  if (value == null || value === "") return null;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-0.5 text-sm text-zinc-100">{value}</div>
    </div>
  );
}

export default function PublicListingPage() {
  const { ulid } = useParams();
  const { t, i18n } = useTranslation(["discover", "enums", "listing"]);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [imgIdx, setImgIdx] = React.useState(0);
  const [leadOpen, setLeadOpen] = React.useState(false);
  const financingSnapshotRef = React.useRef(null);

  const { data: card, isLoading, isError } = useQuery({
    queryKey: ["public-listing", ulid],
    queryFn: () => getPublicListing(ulid),
    enabled: !!ulid && !user,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    trackFunnel("deal_detail_open", { ulid, public: true });
  }, [ulid]);

  if (!loading && user) return <Navigate to={`/listing/${ulid}`} replace />;

  const gateToRegister = (surface, intent) => {
    try {
      localStorage.setItem("da_return_to", `/listing/${ulid}`);
    } catch { /* private mode */ }
    trackFunnel("register_cta", { surface, ulid, intent });
    if (surface === "pro_gate") trackFunnel("pro_gate_view", { surface: "public_detail" });
    navigate("/register");
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10" data-testid="public-listing-loading">
        <div className="h-80 animate-pulse rounded-2xl bg-zinc-900" />
      </div>
    );
  }
  if (isError || !card) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center" data-testid="public-listing-error">
        <div className="text-lg text-zinc-200">{t("detail.notFound")}</div>
        <Link to="/" className="mt-3 inline-block text-sm text-emerald-300 hover:underline">
          {t("detail.backToDiscovery")}
        </Link>
      </div>
    );
  }

  // Anzeigevertrag 29.09.2026 — the detail page used to require a
  // numeric `price_vs_market_pct`, so a card whose number the engine
  // deliberately suppressed (weak/broad cohort) showed NO statement at
  // all — just an orphan "Confidence: weak" line. The shared badge
  // covers all three tiers, so ask IT whether there is something to say.
  const marketBadge = resolveMarketBadge(card);
  const hasMarketInfo = !!marketBadge;
  // Blocked comparable counts (weak/broad basis) stay invisible here too.
  const shownComps = displayComparableCount(card);
  const isAuto = card.vertical === "auto";
  // Aufgabe 11 — same rule as the protected detail page: a price is
  // required and an EXPLICIT rental suppresses financing. No login gate.
  const showFinance = Boolean(
    card.price && card.transaction_type !== "rent"
  );
  const images = card.images || [];
  const loc = card.location_details || {};
  const aiHint =
    (card.ai_insight_i18n && (card.ai_insight_i18n[i18n.language?.slice(0, 2)] ||
      card.ai_insight_i18n.de)) || card.ai_insight_one_liner;

  return (
    <div className="min-h-dvh bg-zinc-950 text-zinc-100" data-testid="public-listing-page">
      <div className="mx-auto max-w-5xl px-4 py-5">
        <button
          type="button"
          data-testid="public-listing-back-btn"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t("detail.backToDiscovery")}
        </button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
          {/* left: gallery + facts — min-w-0 keeps the thumbnail strip from
              widening the column (mobile: single implicit track) */}
          <div className="min-w-0 space-y-4">
            <div className="min-w-0 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
              <div className="aspect-[16/10] w-full">
                {images[imgIdx] && (
                  <img
                    src={images[imgIdx]}
                    alt={card.title || ""}
                    className="h-full w-full object-cover"
                    data-testid="public-listing-main-image"
                  />
                )}
              </div>
              {images.length > 1 && (
                <div className="flex w-full min-w-0 gap-1.5 overflow-x-auto p-2" data-testid="public-listing-thumbs">
                  {images.slice(0, 10).map((src, i) => (
                    <button
                      key={src + i}
                      type="button"
                      onClick={() => setImgIdx(i)}
                      className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border ${
                        i === imgIdx ? "border-emerald-400" : "border-zinc-800"
                      }`}
                    >
                      <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <h1 className="min-w-0 break-words text-xl font-semibold leading-snug text-zinc-50" data-testid="public-listing-title">
              {card.title}
            </h1>
            <div className="flex min-w-0 items-center gap-2 text-sm text-zinc-400">
              <MapPin className="h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{[loc.city, loc.region, card.country].filter(Boolean).join(", ") || card.location}</span>
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {isAuto ? (
                <>
                  <Fact label={t("facts.year")} value={card.year} />
                  <Fact
                    label={t("facts.mileage")}
                    value={card.mileage_km != null ? `${card.mileage_km.toLocaleString("de-AT")} km` : null}
                  />
                  <Fact label={t("facts.fuel")} value={card.fuel ? t(`enums:fuel.${card.fuel}`, { defaultValue: card.fuel }) : null} />
                  <Fact label={t("facts.gearbox")} value={card.gearbox ? t(`enums:gearbox.${card.gearbox}`, { defaultValue: card.gearbox }) : null} />
                </>
              ) : (
                <>
                  <Fact label={t("facts.objectType")} value={card.object_type ? t(`enums:object_type.${card.object_type}`, { defaultValue: card.object_type }) : null} />
                  <Fact label={t("facts.rooms")} value={card.rooms} />
                  <Fact
                    label={t("facts.livingArea")}
                    value={card.living_area_m2 != null ? `${Math.round(card.living_area_m2)} m²` : null}
                  />
                  <Fact
                    label={t("facts.transactionType")}
                    value={card.transaction_type ? t(`filters.${card.transaction_type}`) : null}
                  />
                </>
              )}
            </div>

            {card.description_excerpt && (
              <p className="break-words text-sm leading-relaxed text-zinc-400" data-testid="public-listing-excerpt">
                {card.description_excerpt}
              </p>
            )}
          </div>

          {/* right: price + intelligence + gates */}
          <div className="min-w-0 space-y-4">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5">
              <div className="text-2xl font-bold text-emerald-300" data-testid="public-listing-price">
                {card.price_display || (card.price != null ? `€ ${card.price.toLocaleString("de-AT")}` : "—")}
              </div>
              {hasMarketInfo && (
                <div className="mt-2" data-testid="public-listing-signal">
                  <MarketBadge card={card} t={t} />
                </div>
              )}
              {(card.confidence_band_key || card.confidence_label) && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-500">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                  {t("detail.confidence")}: {card.confidence_band_key
                    ? t(`confidence.${card.confidence_band_key}`, { defaultValue: card.confidence_label })
                    : card.confidence_label}
                </div>
              )}
              {shownComps !== null && (
                <div className="mt-1 text-[11px] text-zinc-500" data-testid="public-listing-comp-count">
                  {t("detail.comparablesCount", { count: shownComps })}
                </div>
              )}
              {!isAuto && typeof card.gross_yield_avg === "number" && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-sky-500/25 bg-sky-500/10 px-3 py-2 text-sm text-sky-200" data-testid="public-listing-yield">
                  <Percent className="h-4 w-4" aria-hidden />
                  {t("detail.grossYield")}: {card.gross_yield_avg.toFixed(1).replace(".", ",")} %
                </div>
              )}
              {aiHint && (
                <div className="mt-3 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 text-xs leading-relaxed text-zinc-300" data-testid="public-listing-ai-hint">
                  {aiHint}
                </div>
              )}
            </div>

            {/* gates */}
            <button
              type="button"
              data-testid="public-listing-source-gate"
              onClick={() => gateToRegister("source", "open_source")}
              className="flex w-full items-center justify-between rounded-2xl bg-amber-500 px-4 py-3.5 text-left transition hover:bg-amber-400"
            >
              <span>
                <span className="block text-sm font-semibold text-zinc-950">
                  {t("detail.openSource")}
                </span>
                <span className="block text-[11px] text-zinc-900/80">{t("detail.openSourceSub")}</span>
              </span>
              <ExternalLink className="h-4 w-4 text-zinc-950" aria-hidden />
            </button>

            <button
              type="button"
              data-testid="public-listing-analysis-gate"
              onClick={() => gateToRegister("pro_gate", "full_analysis")}
              className="flex w-full items-center justify-between rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3.5 text-left transition hover:bg-emerald-500/15"
            >
              <span>
                <span className="block text-sm font-semibold text-emerald-200">
                  {t("detail.fullAnalysis")}
                </span>
                <span className="block text-[11px] text-emerald-200/60">
                  {t("detail.fullAnalysisSub")}
                </span>
              </span>
              <Lock className="h-4 w-4 text-emerald-300" aria-hidden />
            </button>

            <div className="flex gap-2.5">
              <button
                type="button"
                data-testid="public-listing-favorite-gate"
                onClick={() => gateToRegister("favorite", "favourite")}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-zinc-700 px-3 py-2.5 text-sm text-zinc-300 hover:border-rose-400 hover:text-rose-300 transition"
              >
                <Heart className="h-4 w-4" aria-hidden />
                {t("detail.favorite")}
              </button>
              {card.price_history_count > 0 && (
                <button
                  type="button"
                  data-testid="public-listing-history-gate"
                  onClick={() => gateToRegister("price_history", "price_history")}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-zinc-700 px-3 py-2.5 text-sm text-zinc-300 hover:border-emerald-400 hover:text-emerald-300 transition"
                >
                  <LineChart className="h-4 w-4" aria-hidden />
                  {t("detail.priceHistory", { count: card.price_history_count })}
                </button>
              )}
            </div>
            <div className="text-center text-[11px] text-zinc-600">{t("detail.gateNote")}</div>
          </div>
        </div>

        {/* Aufgabe 11 (06.10.2026) — Finanzrechner + Finanzierungsanfrage
            OHNE Konto. Vorher war der Rechner nur auf der geschützten
            Detailseite erreichbar, Gäste kamen gar nicht hinein. Nur das
            Speichern einer Berechnung braucht weiterhin eine Anmeldung. */}
        {showFinance && (
          <div className="mt-8 space-y-4" data-testid="public-listing-finance">
            <FinanceCalculator
              card={card}
              onResult={(out, inputs) => {
                financingSnapshotRef.current = { inputs, output: out };
              }}
            />
            <button
              type="button"
              onClick={() => setLeadOpen(true)}
              data-testid="public-lead-cta"
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-amber-500/60 bg-amber-500 px-5 py-3.5 text-sm font-semibold text-zinc-950 transition hover:border-amber-300 hover:bg-amber-400"
            >
              {/* Live-Abnahme 06.10.2026: der Schlüssel liegt im
                  Namensraum `listing`, die Seite lud ihn nicht — der
                  `defaultValue` sprang ein und der Knopf blieb in JEDER
                  Sprache deutsch (im englischen Aufruf belegt). Der
                  Pfeil steckt bereits in der Übersetzung. */}
              {t("listing:requestFinancing")}
            </button>
            <p
              className="text-center text-[11px] text-zinc-600"
              data-testid="public-finance-save-hint"
            >
              {i18n.t("mobile:lead.saveHistoryLoginHint")}
            </p>
          </div>
        )}
        <LeadModal
          open={leadOpen}
          onClose={() => setLeadOpen(false)}
          card={{ ...card, ulid: card?.id }}
          financingInputs={financingSnapshotRef.current?.inputs}
          financingOutput={financingSnapshotRef.current?.output}
        />

      </div>
    </div>
  );
}
